import { pipe } from 'fp-ts/function';
import { get, identity, map } from 'lodash/fp';
import { createSelector } from 'reselect';
import { IMAGE_DEFAULT_QUALITY } from '@mews-distributor/common';
import { PricingModeKey } from '@mews/enums';
import { DateTimeService, DateTimeUtcService, RouterService, WallClockDateService } from '@mews/framework';

// @ts-expect-error $TSFixMeError: Cannot find module '../images/defaultIntroImage.webp' or its corresponding type declarations.
// Image source: https://cs.m.wikipedia.org/wiki/Soubor:NYC_Top_of_the_Rock_Pano.jpg, https://upload.wikimedia.org/wikipedia/commons/3/39/NYC_Top_of_the_Rock_Pano.jpg
import defaultIntroImage from '../images/defaultIntroImage.webp';

import { getContentUrl, getImageBaseUrl, isMultiHotel } from '../modules/configuration/selectors';
import { getSelectedCityImageId } from '../modules/entities/cities/selectors';
import { getRateName } from '../modules/entities/rates/selectors';
import { createLocalizePropertySelector, localizeProperty } from '../modules/globalization/selectors';
import { getCustomerEmail, getCustomerFirstName, getCustomerLastName } from '../views/checkout/data/selectors';
import { getConfirmedReservationGroupId, getViewConfirmedReservations } from '../views/confirmation/data/selectors';
import {
    getSelectedEnterpriseDefaultLanguageCode,
    getSelectedEnterpriseDefaultRateCurrencyCode,
    getSelectedEnterpriseIntroImageId,
    getSelectedEnterpriseName,
    getSelectedEnterpriseTimezone,
} from './views/selectedEnterprise/selectedEnterprise';

export const getDefaultIntroImageUrl = createSelector(
    getContentUrl,
    (contentUrl) => `${contentUrl}${defaultIntroImage}`
);

export const getIntroImageUrl = createSelector(
    isMultiHotel,
    getImageBaseUrl,
    getSelectedEnterpriseIntroImageId,
    getSelectedCityImageId,
    (multiHotel, imageBaseUrl, hotelImageId, cityImageId) => {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const singleHotelImageId = hotelImageId || cityImageId;

        const imageId = multiHotel ? cityImageId : singleHotelImageId;
        // @ts-expect-error $TSFixMeError: Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return imageId && RouterService.imageUrl(imageId, imageBaseUrl, { quality: IMAGE_DEFAULT_QUALITY });
    }
);

export const parseReservationDate = (dateString: string, timezone: string | undefined) =>
    pipe(
        dateString,
        (date) => DateTimeUtcService.parse(date),
        // For compatibility reasons, keep the ts-expect-error the same we kept it in `getViewConfirmedReservationsWithParsedDates`. That way the output will be the same.
        // @ts-expect-error $TSFixMeError: Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
        (date) => DateTimeService.fromUtc(date, timezone),
        (date) => DateTimeService.toDate(date),
        (date) => WallClockDateService.parse(date.value)
    );

export const getViewConfirmedReservationsWithParsedDates = createSelector(
    getViewConfirmedReservations,
    getSelectedEnterpriseTimezone,
    (reservations, timezone) =>
        map(
            ({ startUtc, endUtc, ...reservation }) => ({
                ...reservation,
                startUtc,
                endUtc,
                // Use from BE when serialized. GX-632
                // timeUnitPeriod: TimeUnitPeriod.Hour,
                startDate: parseReservationDate(startUtc, timezone),
                endDate: parseReservationDate(endUtc, timezone),
            }),
            reservations
        )
);

export const getReservationsIntegrationData = createSelector(
    identity,
    getConfirmedReservationGroupId,
    getViewConfirmedReservationsWithParsedDates,
    getSelectedEnterpriseDefaultLanguageCode,
    getSelectedEnterpriseDefaultRateCurrencyCode,
    getCustomerEmail,
    getCustomerFirstName,
    getCustomerLastName,
    (
        state,
        reservationGroupId,
        reservations,
        defaultLanguageCode,
        defaultCurrencyCode,
        customerEmail,
        customerFirstName,
        customerLastName
    ) =>
        map(({ roomName, id, roomId, rateId, confirmationNumber, amount, startDate, endDate }) => {
            // @ts-expect-error $TSFixMeError: Argument of type 'undefined' is not assignable to parameter of type 'ApplicationState'.
            const rateName = getRateName(state, { rateId });

            return {
                reservationGroupId,
                id,
                roomId,
                rateId,
                rateName: localizeProperty(state, { languageCode: defaultLanguageCode, property: rateName }),
                number: confirmationNumber,
                roomName: localizeProperty(state, { languageCode: defaultLanguageCode, property: roomName }),
                cost: get([defaultCurrencyCode, PricingModeKey.GrossValue], amount),
                startDate: get('value', startDate),
                endDate: get('value', endDate),
                nights: WallClockDateService.diffDays(endDate, startDate),
                customerEmail,
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                customerName: `${customerFirstName || ''} ${customerLastName || ''}`.trim(),
                currencyCode: defaultCurrencyCode,
                alternativeCost: map((a) => get(PricingModeKey.GrossValue, a), amount),
            };
        }, reservations)
);

export const getLocalizedHotelName = createLocalizePropertySelector(getSelectedEnterpriseName);
